import { useCallback, useEffect, useRef, useState } from "react";
import { EmulatorController } from "../emulator/emulator-controller";
import type {
  ServerMessage,
  ExecuteCommandMessage,
  SaveStateInfo,
  PlaybackSpeed,
  ReplayAction,
} from "../server/types";

type WebSocketHook = {
  addHandler: (handler: (msg: ServerMessage) => void) => () => void;
  sendEmulatorReady: () => void;
  sendCommandResult: (
    id: string,
    result: { screenshot?: string; totalFrames: number; success: boolean; error?: string }
  ) => void;
};

export function useEmulator(ws: WebSocketHook) {
  const controllerRef = useRef<EmulatorController | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [ready, setReady] = useState(false);
  const [totalFrames, setTotalFrames] = useState(0);
  const [saveStates, setSaveStates] = useState<SaveStateInfo[]>([]);

  const initEmulator = useCallback(
    async (canvas: HTMLCanvasElement) => {
      if (controllerRef.current) return;

      canvasRef.current = canvas;
      const controller = new EmulatorController();
      controllerRef.current = controller;

      try {
        await controller.init(canvas);
        setReady(true);
        ws.sendEmulatorReady();
        console.log("[useEmulator] Ready");
      } catch (e) {
        console.error("[useEmulator] Failed to init:", e);
      }
    },
    [ws]
  );

  const setSpeed = useCallback((speed: PlaybackSpeed) => {
    controllerRef.current?.setSpeed(speed);
  }, []);

  const pause = useCallback(() => {
    controllerRef.current?.pause();
  }, []);

  const saveReplayStart = useCallback(async () => {
    await controllerRef.current?.saveReplayStart();
  }, []);

  const replayAndRecord = useCallback(
    async (
      actions: ReplayAction[],
      onProgress?: (step: number, total: number) => void
    ): Promise<Blob | null> => {
      if (!controllerRef.current) return null;
      return controllerRef.current.replayAndRecord(actions, onProgress);
    },
    []
  );

  const replay = useCallback(
    async (
      actions: ReplayAction[],
      onProgress?: (step: number, total: number) => void
    ): Promise<boolean> => {
      if (!controllerRef.current) return false;
      return controllerRef.current.replay(actions, onProgress);
    },
    []
  );

  // Handle commands from server
  useEffect(() => {
    const removeHandler = ws.addHandler(async (msg: ServerMessage) => {
      if (msg.type !== "execute_command") return;

      const cmd = msg as ExecuteCommandMessage;
      const controller = controllerRef.current;

      if (!controller) {
        ws.sendCommandResult(cmd.id, {
          totalFrames: 0,
          success: false,
          error: "Emulator not initialized",
        });
        return;
      }

      try {
        switch (cmd.command.type) {
          case "advance_frames": {
            const result = await controller.advanceFrames(
              cmd.command.frames,
              cmd.command.buttons
            );
            setTotalFrames(result.totalFrames);
            ws.sendCommandResult(cmd.id, {
              screenshot: result.screenshot,
              totalFrames: result.totalFrames,
              success: true,
            });
            break;
          }
          case "save_state": {
            const result = await controller.saveState(cmd.command.slotName);
            setTotalFrames(result.totalFrames);
            // Update save states list
            const names = controller.getSaveStateNames();
            setSaveStates(
              names.map((name) => ({
                slotName: name,
                frameNumber: controller.getSaveStateFrame(name) ?? 0,
                timestamp: Date.now(),
              }))
            );
            ws.sendCommandResult(cmd.id, {
              totalFrames: result.totalFrames,
              success: true,
            });
            break;
          }
          case "load_state": {
            const result = await controller.loadState(cmd.command.slotName);
            setTotalFrames(result.totalFrames);
            ws.sendCommandResult(cmd.id, {
              screenshot: result.screenshot,
              totalFrames: result.totalFrames,
              success: true,
            });
            break;
          }
          case "get_screenshot": {
            const result = await controller.getScreenshot();
            setTotalFrames(result.totalFrames);
            ws.sendCommandResult(cmd.id, {
              screenshot: result.screenshot,
              totalFrames: result.totalFrames,
              success: true,
            });
            break;
          }
        }
      } catch (e: unknown) {
        const errorMsg = e instanceof Error ? e.message : "Unknown error";
        console.error("[useEmulator] Command failed:", errorMsg);
        ws.sendCommandResult(cmd.id, {
          totalFrames: controller.getTotalFrames(),
          success: false,
          error: errorMsg,
        });
      }
    });

    return removeHandler;
  }, [ws]);

  // Cleanup
  useEffect(() => {
    return () => {
      controllerRef.current?.destroy();
    };
  }, []);

  return {
    canvasRef,
    initEmulator,
    ready,
    totalFrames,
    saveStates,
    setSpeed,
    pause,
    saveReplayStart,
    replayAndRecord,
    replay,
  };
}
